import { getServiceUrl } from '@airclou/utils';
import { HTTP_TIMEOUT_MS } from '../config';
import { apiErrorFrom } from './errors';

const API_BASE_URL = getServiceUrl('dashboard');

// ============================================
// Types
// ============================================

export interface CompanyGroup {
  id: string;
  name: string;
  description: string | null;
  member_count: number;
  workspace_count: number;
  created_at: string;
  // Nullable column: the route guards it with `.filter(Boolean)` before looking
  // the creator up, and falls back to 'Unknown' for the name — so the name is
  // always a string while the id is not.
  created_by: string | null;
  created_by_name: string;
}

export interface GroupMember {
  id: string;
  user_id: string;
  user: {
    name: string;
    email: string;
    avatar_url?: string;
  };
  added_at: string;
  // Same shape as `created_by`: nullable column, and the route returns a null
  // name alongside it rather than 'Unknown'.
  added_by: string | null;
  added_by_name: string | null;
}

export interface GroupWorkspace {
  workspace_id: string;
  workspace_name: string;
  assigned_at: string;
}

export interface GroupDetail extends Omit<CompanyGroup, 'workspace_count'> {
  updated_at: string;
  members: GroupMember[];
  workspaces: GroupWorkspace[];
}

export interface AvailableUser {
  user_id: string;
  name: string;
  email: string;
  avatar_url?: string;
  company_role: string;
}

export interface CompanyMember {
  user_id: string;
  name: string;
  email: string;
  avatar_url?: string;
  company_role: 'admin' | 'member';
  status: string;
  joined_at: string;
}

export interface CompanyInvitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  invited_by: string;
  invited_by_name: string;
  message?: string;
  created_at: string;
  expires_at: string;
}

// ============================================
// Auth helper
// ============================================

function getAuthToken(): string | null {
  return localStorage.getItem('dashboard:authToken');
}

function authHeaders(): Record<string, string> {
  return {
    'Authorization': `Bearer ${getAuthToken()}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Turns a failed response into an `ApiError` — rather than a decorated plain
 * Error — because it carries the status, which is what lets a detail page tell
 * "this group is gone" (404, a retry cannot help) from "the request failed" (keep
 * the page, offer a retry). The server's `code` rides along for the modals that
 * branch on it.
 */
async function assertOk(response: Response): Promise<void> {
  if (!response.ok) {
    throw await apiErrorFrom(response, `Request failed with status ${response.status}`);
  }
}

/**
 * Unwraps one `{ success, data }` answer into its payload.
 *
 * The envelope is this client's business, not its callers': they get the payload
 * and catch errors, never `res.data.whatever`. This client used to return the
 * envelope raw, so every call site dug a level deeper than it should have.
 *
 * Use `handlePaginated` instead where the answer carries `meta`, which has to
 * survive the unwrap.
 */
async function handleResponse<T>(response: Response): Promise<T> {
  await assertOk(response);

  // Every route this client calls answers with a body today, including the
  // deletes (`{ message: '...' }`). The short-circuit is here so that switching
  // one of them to `noContent` cannot turn a successful call into a JSON parse
  // error at the caller.
  if (response.status === 204) return undefined as T;

  const json = await response.json();
  return json.data as T;
}

/**
 * Same, for a paginated answer: `success` is dropped, `meta` is kept beside the
 * rows because the caller needs it to render pagination.
 */
async function handlePaginated<T>(
  response: Response
): Promise<{ data: T[]; meta: { total: number; page: number; limit: number; totalPages: number } }> {
  await assertOk(response);
  const json = await response.json();
  return { data: json.data, meta: json.meta };
}

// ============================================
// Company Members API
// ============================================

export async function listCompanyMembers(
  companyId: string,
  params?: { search?: string; role?: string; status?: string; page?: number; limit?: number }
): Promise<{ data: CompanyMember[]; meta: { total: number; page: number; limit: number; totalPages: number } }> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set('search', params.search);
  if (params?.role) searchParams.set('role', params.role);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const url = `${API_BASE_URL}/api/companies/${companyId}/members?${searchParams.toString()}`;
  const response = await fetch(url, { headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
  return handlePaginated<CompanyMember>(response);
}

export async function updateCompanyMember(
  companyId: string,
  userId: string,
  data: { role?: string; status?: string }
): Promise<{ member: CompanyMember }> {
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/members/${userId}`,
    { method: 'PATCH', headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS), body: JSON.stringify(data) }
  );
  return handleResponse(response);
}

export async function removeCompanyMember(companyId: string, userId: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/members/${userId}`,
    { method: 'DELETE', headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) }
  );
  await handleResponse(response);
}

// ============================================
// Company Invitations API
// ============================================

export async function createInvitation(
  companyId: string,
  data: { email: string; role: string; message?: string }
): Promise<{ invitation: CompanyInvitation }> {
  const response = await fetch(`${API_BASE_URL}/api/companies/${companyId}/invitations`, {
    method: 'POST',
    headers: authHeaders(),
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function listInvitations(
  companyId: string,
  status?: string
): Promise<{ invitations: CompanyInvitation[] }> {
  const url = status
    ? `${API_BASE_URL}/api/companies/${companyId}/invitations?status=${status}`
    : `${API_BASE_URL}/api/companies/${companyId}/invitations`;
  const response = await fetch(url, { headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
  return handleResponse(response);
}

export async function resendInvitation(companyId: string, invitationId: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/invitations/${invitationId}/resend`,
    { method: 'POST', headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) }
  );
  await handleResponse(response);
}

export async function cancelInvitation(companyId: string, invitationId: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/invitations/${invitationId}`,
    { method: 'DELETE', headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) }
  );
  await handleResponse(response);
}

// ============================================
// Company Groups API
// ============================================

export async function listGroups(
  companyId: string,
  params?: { search?: string }
): Promise<{ groups: CompanyGroup[] }> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set('search', params.search);

  const url = `${API_BASE_URL}/api/companies/${companyId}/groups?${searchParams.toString()}`;
  const response = await fetch(url, { headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
  return handleResponse(response);
}

export async function getGroupDetails(
  companyId: string,
  groupId: string
): Promise<{ group: GroupDetail }> {
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/groups/${groupId}`,
    { headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) }
  );
  return handleResponse(response);
}

/**
 * The route answers with the new row itself — `created(res, { ...row, counts })` —
 * not a `{ group }` wrapper. This said `{ group: CompanyGroup }` for as long as it
 * existed and nobody noticed, because both callers await it for the error and then
 * reload; anyone who read the value would have got `undefined`.
 */
export async function createGroup(
  companyId: string,
  data: { name: string; description?: string }
): Promise<CompanyGroup> {
  const response = await fetch(`${API_BASE_URL}/api/companies/${companyId}/groups`, {
    method: 'POST',
    headers: authHeaders(),
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

/**
 * Answers with the updated row itself (`success(res, result.rows[0])`), and only
 * the columns the UPDATE returned — id, name, description, updated_at — so it is
 * narrower than a full `CompanyGroup`. Same history as `createGroup`: the old
 * `{ group }` type was never what came back.
 */
export async function updateGroup(
  companyId: string,
  groupId: string,
  data: { name?: string; description?: string }
): Promise<Pick<CompanyGroup, 'id' | 'name' | 'description'> & { updated_at: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/groups/${groupId}`,
    { method: 'PATCH', headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS), body: JSON.stringify(data) }
  );
  return handleResponse(response);
}

export async function deleteGroup(companyId: string, groupId: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/groups/${groupId}`,
    { method: 'DELETE', headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) }
  );
  await handleResponse(response);
}

export async function addGroupMembers(
  companyId: string,
  groupId: string,
  userIds: string[]
): Promise<{ added: string[]; already_members: string[]; not_in_company: string[] }> {
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/groups/${groupId}/members`,
    { method: 'POST', headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS), body: JSON.stringify({ user_ids: userIds }) }
  );
  return handleResponse(response);
}

export async function removeGroupMember(
  companyId: string,
  groupId: string,
  userId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/groups/${groupId}/members/${userId}`,
    { method: 'DELETE', headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) }
  );
  await handleResponse(response);
}

export async function listAvailableUsers(
  companyId: string,
  groupId: string,
  search?: string
): Promise<{ users: AvailableUser[] }> {
  const url = search
    ? `${API_BASE_URL}/api/companies/${companyId}/groups/${groupId}/available-users?search=${encodeURIComponent(search)}`
    : `${API_BASE_URL}/api/companies/${companyId}/groups/${groupId}/available-users`;
  const response = await fetch(url, { headers: authHeaders(), signal: AbortSignal.timeout(HTTP_TIMEOUT_MS) });
  return handleResponse(response);
}
